import { api, makeApiRequest } from 'api/_init'
import { unwrapEnvelope } from 'domain/shared/envelope'
import {
  mapSetupProcessResponse,
  mapSetupProcessPreviewResponse,
  upsertIntegrationDetail,
} from './mappers'

function toDateOnly(value) {
  return typeof value === 'string' ? value.slice(0, 10) : value
}

// conversion_id/lead_form_id/custom_event_type are no longer flat v4 request
// fields; they fold back into integration_details as Meta entries.
function buildIntegrationDetails({
  integration_details,
  conversion_id,
  lead_form_id,
  custom_event_type,
}) {
  let details = Array.isArray(integration_details) ? integration_details : []

  const inlineAssets = [
    { platform: 'facebook', asset_class: 'conversion', asset_id: conversion_id },
    { platform: 'facebook', asset_class: 'lead_form', asset_id: lead_form_id },
    { platform: 'facebook', asset_class: 'custom_event_type', asset_id: custom_event_type },
  ]

  for (const entry of inlineAssets) {
    details = upsertIntegrationDetail(details, entry)
  }

  return details
}

function toRequestBody({
  goal,
  conversion_id,
  lead_form_id,
  custom_event_type,
  total_budget,
  start_date,
  end_date,
  creatives,
  integration_details,
  targeting = [],
  platform,
  platform_overrides,
  campaign_targeting,
  dsa_payor,
  dsa_beneficiary,
}) {
  return {
    goal,
    total_budget,
    start_date: toDateOnly(start_date),
    end_date: toDateOnly(end_date),
    creatives,
    targeting,
    ...(platform ? { platform } : {}),
    ...(platform_overrides ? { platform_overrides } : {}),
    ...(campaign_targeting ? { campaign_targeting } : {}),
    integration_details: buildIntegrationDetails({
      integration_details,
      conversion_id,
      lead_form_id,
      custom_event_type,
    }),
    dsa_payor: dsa_payor ?? null,
    dsa_beneficiary: dsa_beneficiary ?? null,
  }
}

let setupController

const campaignSetupApi = {
  fetchAsync: async (eventId) => {
    const response = await makeApiRequest(api, 'get', `/setup_processes/${eventId}`, null)
    const { data, error, status } = unwrapEnvelope(response)
    if (status === 200) {
      return { data: mapSetupProcessResponse(data, eventId), error, status }
    }

    return { error, status }
  },

  createAsync: async (eventId, payload) => {
    const response = await makeApiRequest(
      api,
      'post',
      `/setup_processes/${eventId}`,
      toRequestBody(payload),
    )
    const { data, error, status } = unwrapEnvelope(response)
    if (status === 200) {
      return { data: mapSetupProcessResponse(data, eventId), error, status }
    }

    return { error, status, data }
  },

  updateAsync: async (eventId, payload) => {
    if (setupController) {
      setupController.abort()
    } else {
      setupController = new AbortController()
    }

    const response = await makeApiRequest(
      api,
      'put',
      `/setup_processes/${eventId}`,
      toRequestBody(payload),
      { signal: setupController.signal },
    )
    const { data, error, status } = unwrapEnvelope(response)
    if (status === 200) {
      return { data: mapSetupProcessResponse(data, eventId), error, status }
    }

    return { error, status, data }
  },

  deleteAsync: async (eventId, cancel) => {
    const response = await makeApiRequest(
      api,
      'delete',
      `/setup_processes/${eventId}?cancel=${cancel}`,
      null,
    )
    const { error, status } = unwrapEnvelope(response)

    return { error, status }
  },

  previewAsync: async (eventId) => {
    const response = await makeApiRequest(api, 'get', `/setup_processes/${eventId}/preview`, null)
    const { data, error, status } = unwrapEnvelope(response)
    if (status === 200) {
      return { data: mapSetupProcessPreviewResponse(data), error, status }
    }

    return { error, status }
  },

  publishAsync: async (eventId) => {
    if (setupController) {
      setupController.abort()
    }

    const response = await makeApiRequest(api, 'post', `/setup_processes/${eventId}/boost`)
    const { error, status } = unwrapEnvelope(response)

    return { error, status }
  },
}

export default campaignSetupApi
